// Every user-facing string resolves through here (build/lexicon.json, rule 4).
// No screen contains an inline caption. The resolver is style-parameterised —
// Simple renders the `simple` column, and a future theme is one prop change, not
// a rewrite. That is the whole reason the copy lives in data.

import { createContext, useContext, type ReactNode } from 'react';
import type { Style } from './types';

type Subs = Record<string, string | number>;
type KeyTable = Record<string, Partial<Record<Style, string>>>;

export interface LexiconData {
  keys: KeyTable;
}

export type TFn = (key: string, subs?: Subs) => string;

/** Fetch the shipped lexicon (public/lexicon.json). Called once before render. */
export async function loadLexicon(): Promise<LexiconData> {
  const res = await fetch('/lexicon.json', { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`lexicon.json failed to load (${res.status})`);
  return (await res.json()) as LexiconData;
}

function substitute(template: string, subs?: Subs): string {
  if (!subs) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in subs ? String(subs[name]) : whole,
  );
}

/**
 * Build a resolver bound to one style. A missing key returns `⟦key⟧` so an
 * unmapped string is loud in the UI during development rather than silently
 * blank — surfacing exactly the "badly named concept key" the spec warns about.
 */
export function makeT(data: LexiconData, style: Style): TFn {
  return (key, subs) => {
    const entry = data.keys[key];
    const template = entry && (entry[style] ?? entry.simple);
    if (template == null) return `⟦${key}⟧`;
    return substitute(template, subs);
  };
}

const LexiconContext = createContext<TFn | null>(null);

export function LexiconProvider({
  data,
  style,
  children,
}: {
  data: LexiconData;
  style: Style;
  children: ReactNode;
}) {
  return <LexiconContext.Provider value={makeT(data, style)}>{children}</LexiconContext.Provider>;
}

/** The resolver, e.g. `const t = useT(); t('phase.points.cta')`. */
export function useT(): TFn {
  const t = useContext(LexiconContext);
  if (!t) throw new Error('useT must be used inside <LexiconProvider>');
  return t;
}
